import type { HREmployee, HRPerformanceReview } from '@cloud/db'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle, Send } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge, type StatusType } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

const reviewSchema = z.object({
  goals: z.array(z.string()).optional(),
  strengths: z.string().optional(),
  areasOfImprovement: z.string().optional(),
  overallRating: z.number().min(1).max(5).optional(),
  comments: z.string().optional(),
})

type ReviewFormValues = z.infer<typeof reviewSchema>

export const Route = createFileRoute('/hr/performance-reviews/$reviewId')({
  component: PerformanceReviewDetailPage,
})

function PerformanceReviewDetailPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <PerformanceReviewDetail />
    </ModuleGuard>
  )
}

function PerformanceReviewDetail() {
  const params = Route.useParams()
  const reviewId = Number(params.reviewId)

  const {
    data: review,
    isLoading,
    refetch,
  } = useQuery(orpc.hr.getPerformanceReview.queryOptions({ input: { id: reviewId } }))
  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 100 } }),
  )

  const employees = (employeesData?.data ?? []) as HREmployee[]
  const data = review as
    | (HRPerformanceReview & {
        employee?: HREmployee
        reviewer?: HREmployee
      })
    | null

  const updateMutation = useMutation(
    orpc.hr.updatePerformanceReview.mutationOptions({
      onSuccess: () => {
        toast.success('Review updated')
        refetch()
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const submitMutation = useMutation(
    orpc.hr.submitPerformanceReview.mutationOptions({
      onSuccess: () => {
        toast.success('Review submitted')
        refetch()
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const acknowledgeMutation = useMutation(
    orpc.hr.acknowledgePerformanceReview.mutationOptions({
      onSuccess: () => {
        toast.success('Review acknowledged')
        refetch()
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const getEmployeeName = (id: number) => {
    const emp = employees.find((e) => e.id === id)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`
  }

  const isReadOnly = data?.status === 'acknowledged'
  const isEditable = data?.status === 'draft' || data?.status === 'submitted'

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      overallRating: data?.overallRating ?? 1,
      strengths: data?.strengths ?? '',
      areasOfImprovement: data?.areasOfImprovement ?? '',
      comments: data?.comments ?? '',
    },
  })

  const handleSubmit = async (formData: ReviewFormValues) => {
    await updateMutation.mutateAsync({ id: reviewId, ...formData })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p>Performance review not found</p>
      </div>
    )
  }

  const goals = data.goals
    ? typeof data.goals === 'string'
      ? JSON.parse(data.goals)
      : data.goals
    : []

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Performance Review</h1>
            <p className="text-muted-foreground">
              {getEmployeeName(data.employeeId)} - {data.reviewCycle.replace('-', '/')} Review
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={data.status as StatusType} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Employee</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{getEmployeeName(data.employeeId)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Reviewer</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{getEmployeeName(data.reviewerId)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Review Period</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">
              {data.reviewPeriodStart && data.reviewPeriodEnd
                ? `${new Date(data.reviewPeriodStart).toLocaleDateString()} - ${new Date(data.reviewPeriodEnd).toLocaleDateString()}`
                : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {isReadOnly ? (
        <Card>
          <CardHeader>
            <CardTitle>Review Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-medium mb-2">Overall Rating</h4>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`text-2xl ${star <= (data.overallRating ?? 0) ? `text-yellow-500` : `text-gray-300`}`}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>

            {goals.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Goals</h4>
                <ul className="list-disc list-inside space-y-1">
                  {goals.map((goal: string, index: number) => (
                    <li key={index} className="text-muted-foreground">
                      {goal}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.strengths && (
              <div>
                <h4 className="font-medium mb-2">Strengths</h4>
                <p className="text-muted-foreground whitespace-pre-wrap">{data.strengths}</p>
              </div>
            )}

            {data.areasOfImprovement && (
              <div>
                <h4 className="font-medium mb-2">Areas of Improvement</h4>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {data.areasOfImprovement}
                </p>
              </div>
            )}

            {data.comments && (
              <div>
                <h4 className="font-medium mb-2">Comments</h4>
                <p className="text-muted-foreground whitespace-pre-wrap">{data.comments}</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Review Details</CardTitle>
                <CardDescription>
                  {data.status === 'draft'
                    ? 'Edit the performance review details'
                    : 'View and acknowledge the review'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="overallRating"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Overall Rating (1-5)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={5}
                          {...field}
                          onChange={(e) =>
                            field.onChange(e.target.value ? Number(e.target.value) : undefined)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="strengths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Strengths</FormLabel>
                      <FormControl>
                        <Textarea placeholder="List employee strengths..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="areasOfImprovement"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Areas of Improvement</FormLabel>
                      <FormControl>
                        <Textarea placeholder="List areas for improvement..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="comments"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Comments</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Additional comments..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <div className="flex items-center justify-end gap-2">
              {data.status === 'draft' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => submitMutation.mutate({ id: reviewId })}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit Review
                </Button>
              )}
              {data.status === 'submitted' && (
                <Button type="button" onClick={() => acknowledgeMutation.mutate({ id: reviewId })}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Acknowledge Review
                </Button>
              )}
              {isEditable && (
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </div>
          </form>
        </Form>
      )}
    </div>
  )
}
